import React, { Dispatch, FC, SetStateAction, useCallback, useMemo, useState } from 'react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { useController, useFormContext } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import classNames from 'classnames';
import Select from 'react-select';

import { generateSelectStyles } from 'buyside/components/presentation/Fields/selectField';

import { debounce } from 'buyside/util/lodashAlt';
import { IdeaFormInput, userCreditDataDefaultValues } from 'buyside/util/researchSubmission';

import {
  IdeaSubmissionAssetFragment,
  Maybe,
  useIdeaSubmissionAssetLookupQuery,
  useSecuritySearchLazyQuery
} from 'interfaces/graphql-typings';

import fieldStyles from 'buyside/components/presentation/Fields/fieldStyles.scss';
import styles from '../positionsStyles.scss';

const { assetClasses } = require('buyside/util/constantsFromRails.json');

const AssetLookupField: FC<{
  index: number;
  setAsset: Dispatch<SetStateAction<Maybe<IdeaSubmissionAssetFragment>>>;
}> = ({ index, setAsset }) => {
  const { setValue } = useFormContext<IdeaFormInput>();

  const {
    field: { onChange: setAssetId, ref, onBlur },
    fieldState: { error: assetIdError }
  } = useController({
    name: `positions.${index}.assetId`
  });

  const [selectedAssetClass, setSelectedAssetClass] = useState<
    Maybe<{
      id: string;
      name: string;
    }>
  >(assetClasses.find((assetClass: { id: string; name: string }) => assetClass.name === 'Equity'));

  const [selectedSecurity, setSelectedSecurity] = useState<
    Maybe<{
      id: string;
      ticker: string;
      entity_name: string;
      isNewSecurity?: boolean;
    }>
  >();

  const identifier = useMemo(
    () =>
      ['Corporate Debt', 'Mortgage', 'Preferred', 'Sovereign Debt'].some(
        assetClassName => assetClassName === selectedAssetClass?.name
      )
        ? 'CUSIP'
        : 'symbol',
    [selectedAssetClass]
  );

  const [handleSearchSecurity] = useSecuritySearchLazyQuery();

  const { loading: assetLoading } = useIdeaSubmissionAssetLookupQuery({
    variables: { fsymId: `${selectedSecurity?.id}` },
    skip: !selectedSecurity?.id,
    onCompleted: data => {
      const asset = data.ideaSubmissionAssetLookup;

      if (asset) {
        setAsset(asset);
        setAssetId(asset.id);
      }
    },
    onError: () => {
      setSelectedSecurity(null);

      toast.error(
        'There was an issue loading this security, please try again or contact support@sumzero.com',
        {
          autoClose: 7500,
          theme: 'colored'
        }
      );
    }
  });

  const debouncedLoadSecurities = debounce(
    (assetClass, query, callback) =>
      handleSearchSecurity({
        variables: {
          assetClass,
          query,
          skip: false
        }
      }).then(response => {
        const securitySearch = response.data?.securitySearch;

        if (!securitySearch) return callback([]);

        const parsedSearch = JSON.parse(securitySearch);

        if (!parsedSearch) return callback([]);

        let options = [
          {
            label: 'Exact Ticker Match',
            options: parsedSearch.exact_matches
          },
          {
            label: 'Partially Matching Tickers',
            options: parsedSearch.matching_tickers
          },
          {
            label: 'Matching Companies by Name',
            options: parsedSearch.matching_names
          }
        ];

        if (!parsedSearch.exact_matches.length) {
          options = [
            ...options,
            {
              label: "Can't find the security you're looking for?",
              options: [
                {
                  id: '',
                  ticker: query.toUpperCase(),
                  entity_name: '',
                  isNewSecurity: true
                }
              ]
            }
          ];
        }

        return callback(options);
      }),
    1000
  );

  const debouncedLoadSecuritiesCallback = useCallback(
    (inputValue: string, callback: (options: any[]) => void) =>
      debouncedLoadSecurities(selectedAssetClass?.name, inputValue, callback),
    [debouncedLoadSecurities, selectedAssetClass]
  );

  return (
    <>
      <div className={styles.positionRow}>
        <div className={classNames(fieldStyles.field, fieldStyles.medium)}>
          <label data-title htmlFor={`asset-class-${index}`}>
            Asset Class
          </label>
          <Select
            classNamePrefix={`asset-class-${index}`}
            inputId={`asset-class-${index}`}
            isDisabled={assetLoading}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.id}
            isSearchable={false}
            onChange={setSelectedAssetClass}
            options={assetClasses}
            styles={generateSelectStyles({ size: 'medium' }) as any}
            value={selectedAssetClass}
          />
        </div>
      </div>
      <div className={classNames(fieldStyles.field, fieldStyles.medium)}>
        <label data-title htmlFor={`security-search-${index}`}>
          Security Search by {identifier}
        </label>
        <AsyncSelect
          classNamePrefix={`security-search-${index}`}
          components={{ DropdownIndicator: null }}
          getOptionLabel={option =>
            option.isNewSecurity
              ? `${option.ticker} - New Security`
              : `${option.ticker} - ${option.entity_name}`
          }
          getOptionValue={option => option.id}
          inputId={`security-search-${index}`}
          isDisabled={assetLoading}
          isLoading={assetLoading}
          loadOptions={debouncedLoadSecuritiesCallback}
          noOptionsMessage={({ inputValue }) =>
            inputValue ? null : `Start typing a ${identifier} to search for securities`
          }
          onBlur={onBlur}
          onChange={option => {
            if (option?.isNewSecurity) {
              setValue(`positions.${index}.userDefinedAsset`, {
                assetClassId: selectedAssetClass?.id || '',
                currencyId: '',
                entityName: '',
                symbol: option.ticker,
                recentPrice: null,
                userCreditData:
                  selectedAssetClass?.name === 'Corporate Debt' ? userCreditDataDefaultValues : null
              });
            } else {
              setValue(`positions.${index}.userDefinedAsset`, null);
            }
            setSelectedSecurity(option);
          }}
          placeholder="Search and Select..."
          ref={ref}
          styles={generateSelectStyles({ error: Boolean(assetIdError), size: 'medium' }) as any}
          value={selectedSecurity}
        />
        {assetIdError ? (
          <label data-error>
            <FontAwesomeIcon icon={faExclamationCircle} />
            {assetIdError.message}
          </label>
        ) : null}
      </div>
    </>
  );
};

export default AssetLookupField;
